import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { facebookAPI, nangoAPI, clientsAPI } from '../lib/api';
import Nango from '@nangohq/frontend';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import {
  Facebook,
  Instagram,
  MessageSquare,
  Send,
  Calendar,
  BarChart3,
  Heart,
  MessageCircle,
  Share2,
  Eye,
  Users,
  TrendingUp,
  RefreshCw,
  Plus,
  Link2,
  Image,
  Video,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ExternalLink,
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export function SocialMediaManager() {
  const [activeTab, setActiveTab] = useState('pages');
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [posts, setPosts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [comments, setComments] = useState([]);
  const [insights, setInsights] = useState([]);
  const [clients, setClients] = useState([]);
  const [connections, setConnections] = useState([]);
  const [error, setError] = useState(null);
  
  // New post dialog
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPostMessage, setNewPostMessage] = useState('');
  const [newPostLink, setNewPostLink] = useState('');
  const [schedulePost, setSchedulePost] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [posting, setPosting] = useState(false);
  
  // Reply dialog
  const [replyTo, setReplyTo] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replying, setReplying] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedPage) {
      loadPageData(selectedPage.id);
    }
  }, [selectedPage]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pagesData, clientsData, connsData] = await Promise.all([
        facebookAPI.getPages().catch(() => ({ pages: [] })),
        clientsAPI.getAll().catch(() => []),
        nangoAPI.getConnections().catch(() => ({ connections: [] })),
      ]);
      
      setPages(pagesData.pages || []);
      setClients(clientsData || []);
      setConnections(connsData.connections || []);
      
      if (pagesData.pages?.length > 0) {
        setSelectedPage(pagesData.pages[0]);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const loadPageData = async (pageId) => {
    try {
      const [postsData, messagesData, commentsData, insightsData] = await Promise.all([
        facebookAPI.getPagePosts(pageId).catch(() => ({ posts: [] })),
        facebookAPI.getMessages(pageId).catch(() => ({ conversations: [] })),
        facebookAPI.getComments(pageId).catch(() => ({ comments: [] })),
        facebookAPI.getPageInsights(pageId).catch(() => ({ insights: [] })),
      ]);
      
      setPosts(postsData.posts || []);
      setMessages(messagesData.conversations || []);
      setComments(commentsData.comments || []);
      setInsights(insightsData.insights || []);
    } catch (err) {
      console.error('Error loading page data:', err);
    }
  };

  const handleConnectFacebook = async () => {
    setConnecting(true);
    setError(null);
    
    try {
      const sessionData = await nangoAPI.createSession(['facebook']);
      
      if (!sessionData.token) {
        throw new Error('No se pudo crear la sesión de conexión');
      }
      
      // Use connect_link if available (more reliable than SDK popup)
      if (sessionData.connect_link) {
        // Open in new window
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const popup = window.open(
          sessionData.connect_link,
          'nango_connect',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
        );
        
        if (!popup) {
          throw new Error('El popup fue bloqueado. Por favor permite los popups para este sitio.');
        }
        
        // Poll for popup close and check for new connections
        const pollInterval = setInterval(async () => {
          if (popup.closed) {
            clearInterval(pollInterval);
            setConnecting(false);
            // Reload data to check if connection was successful
            await loadData();
          }
        }, 1000);
        
        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          setConnecting(false);
        }, 300000);
        
      } else {
        // Fallback to Nango SDK
        const nango = new Nango({ connectSessionToken: sessionData.token });
        const result = await nango.auth('facebook');
        
        if (result && result.connectionId) {
          await facebookAPI.connect(result.connectionId);
          await loadData();
        }
        setConnecting(false);
      }
    } catch (err) {
      console.error('Connection error:', err);
      setConnecting(false);
      if (err.message?.includes('window_closed') || err.message?.includes('popup')) {
        setError('La ventana fue cerrada. Intenta de nuevo.');
      } else if (err.message?.includes('blocked')) {
        setError('El popup fue bloqueado por el navegador. Por favor permite los popups.');
      } else {
        setError(`Error al conectar: ${err.message || 'Intenta de nuevo'}`);
      }
    }
  };

  const handleCreatePost = async () => {
    if (!selectedPage || !newPostMessage.trim()) return;
    
    setPosting(true);
    try {
      let scheduledTime = null;
      if (schedulePost && scheduledDate) {
        scheduledTime = Math.floor(new Date(scheduledDate).getTime() / 1000);
      }
      
      await facebookAPI.createPost(
        selectedPage.id,
        newPostMessage,
        scheduledTime,
        newPostLink || null
      );
      
      setShowNewPost(false);
      setNewPostMessage('');
      setNewPostLink('');
      setSchedulePost(false);
      setScheduledDate('');
      
      // Reload posts
      await loadPageData(selectedPage.id);
    } catch (err) {
      setError(`Error creando publicación: ${err.message}`);
    } finally {
      setPosting(false);
    }
  };

  const handleReplyToMessage = async () => {
    if (!replyTo || !replyMessage.trim() || !selectedPage) return;
    
    setReplying(true);
    try {
      await facebookAPI.replyToMessage(replyTo.id, selectedPage.id, replyMessage);
      setReplyTo(null);
      setReplyMessage('');
      await loadPageData(selectedPage.id);
    } catch (err) {
      setError(`Error enviando respuesta: ${err.message}`);
    } finally {
      setReplying(false);
    }
  };

  const handleReplyToComment = async (commentId) => {
    if (!replyMessage.trim() || !selectedPage) return;
    
    setReplying(true);
    try {
      await facebookAPI.replyToComment(commentId, selectedPage.id, replyMessage);
      setReplyTo(null);
      setReplyMessage('');
      await loadPageData(selectedPage.id);
    } catch (err) {
      setError(`Error respondiendo comentario: ${err.message}`);
    } finally {
      setReplying(false);
    }
  };

  const hasFacebookConnection = connections.some(c => 
    c.provider_config_key === 'facebook' || c.integration_id === 'facebook'
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // No Facebook connection - show connect button
  if (!hasFacebookConnection || pages.length === 0) {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-6"
      >
        <motion.div variants={itemVariants}>
          <h1 className="text-3xl font-bold tracking-tight">Gestión de Redes Sociales</h1>
          <p className="text-muted-foreground mt-1">Conecta Facebook e Instagram para gestionar contenido</p>
        </motion.div>

        <motion.div variants={itemVariants}>
          <Card className="max-w-lg mx-auto">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <Facebook className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Conecta tu cuenta de Facebook</h2>
              <p className="text-muted-foreground mb-6">
                Conecta tu cuenta para gestionar páginas, publicaciones, mensajes y comentarios.
              </p>
              
              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {error}
                </div>
              )}
              
              <Button 
                onClick={handleConnectFacebook}
                disabled={connecting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {connecting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Conectando...
                  </>
                ) : (
                  <>
                    <Facebook className="w-4 h-4 mr-2" />
                    Conectar con Facebook
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
      data-testid="social-media-manager"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Gestión de Redes Sociales</h1>
          <p className="text-muted-foreground mt-1">Facebook & Instagram</p>
        </div>
        <div className="flex items-center gap-3">
          <Select 
            value={selectedPage?.id || ''} 
            onValueChange={(value) => setSelectedPage(pages.find(p => p.id === value))}
          >
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="Selecciona una página" />
            </SelectTrigger>
            <SelectContent>
              {pages.map((page) => (
                <SelectItem key={page.id} value={page.id}>
                  <div className="flex items-center gap-2">
                    <Facebook className="w-4 h-4 text-blue-600" />
                    {page.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => loadPageData(selectedPage?.id)}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setShowNewPost(true)} className="gradient-primary text-white">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Publicación
          </Button>
        </div>
      </motion.div>

      {/* Error Alert */}
      {error && (
        <motion.div variants={itemVariants}>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-700">{error}</p>
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>Cerrar</Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Page Info & Stats */}
      {selectedPage && (
        <motion.div variants={itemVariants} className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-100">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {insights.find(i => i.name === 'page_follows')?.values?.[0]?.value?.toLocaleString() || '-'}
                </p>
                <p className="text-sm text-muted-foreground">Seguidores</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-full bg-emerald-100">
                <Eye className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {insights.find(i => i.name === 'page_media_view')?.values?.[0]?.value?.toLocaleString() || '-'}
                </p>
                <p className="text-sm text-muted-foreground">Visualizaciones</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-full bg-purple-100">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {insights.find(i => i.name === 'page_post_engagements')?.values?.[0]?.value?.toLocaleString() || '-'}
                </p>
                <p className="text-sm text-muted-foreground">Engagement</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-full bg-orange-100">
                <MessageSquare className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{messages.length}</p>
                <p className="text-sm text-muted-foreground">Conversaciones</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Instagram Account */}
      {selectedPage?.instagram_business_account && (
        <motion.div variants={itemVariants}>
          <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Avatar className="w-12 h-12 border-2 border-white">
                  <AvatarImage src={selectedPage.instagram_business_account.profile_picture_url} />
                  <AvatarFallback><Instagram className="w-6 h-6" /></AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">@{selectedPage.instagram_business_account.username}</p>
                  <p className="text-sm text-white/80">Cuenta de Instagram conectada</p>
                </div>
              </div>
              <Badge className="bg-white/20 text-white">Instagram Business</Badge>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Tabs */}
      <motion.div variants={itemVariants}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="posts">Publicaciones</TabsTrigger>
            <TabsTrigger value="messages">Mensajes</TabsTrigger>
            <TabsTrigger value="comments">Comentarios</TabsTrigger>
            <TabsTrigger value="insights">Analytics</TabsTrigger>
          </TabsList>

          {/* Posts Tab */}
          <TabsContent value="posts" className="mt-6">
            <div className="grid gap-4">
              {posts.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    No hay publicaciones recientes
                  </CardContent>
                </Card>
              ) : (
                posts.map((post) => (
                  <Card key={post.id} className="card-hover">
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {post.full_picture && (
                          <img 
                            src={post.full_picture} 
                            alt="Post" 
                            className="w-32 h-32 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1">
                          <p className="text-sm mb-3">{post.message || '(Sin texto)'}</p>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Heart className="w-4 h-4" />
                              {post.likes?.summary?.total_count || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageCircle className="w-4 h-4" />
                              {post.comments?.summary?.total_count || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <Share2 className="w-4 h-4" />
                              {post.shares?.count || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {new Date(post.created_time).toLocaleDateString('es-ES')}
                            </span>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" asChild>
                          <a href={post.permalink_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="mt-6">
            <div className="grid gap-4">
              {messages.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    No hay conversaciones
                  </CardContent>
                </Card>
              ) : (
                messages.map((conv) => (
                  <Card key={conv.id} className="card-hover">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <Avatar>
                            <AvatarFallback>
                              {conv.participants?.data?.[0]?.name?.[0] || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">
                              {conv.participants?.data?.map(p => p.name).join(', ') || 'Usuario'}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {conv.messages?.data?.[0]?.message || 'Sin mensajes'}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {conv.updated_time && new Date(conv.updated_time).toLocaleString('es-ES')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {conv.unread_count > 0 && (
                            <Badge className="bg-red-500">{conv.unread_count}</Badge>
                          )}
                          <Button 
                            size="sm"
                            onClick={() => setReplyTo({ type: 'message', ...conv })}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Responder
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Comments Tab */}
          <TabsContent value="comments" className="mt-6">
            <div className="grid gap-4">
              {comments.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    No hay comentarios recientes
                  </CardContent>
                </Card>
              ) : (
                comments.map((comment) => (
                  <Card key={comment.id} className="card-hover">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <Avatar>
                            <AvatarFallback>
                              {comment.from?.name?.[0] || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{comment.from?.name || 'Usuario'}</p>
                            <p className="text-sm mt-1">{comment.message}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              En: "{comment.post_message}..."
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(comment.created_time).toLocaleString('es-ES')}
                            </p>
                          </div>
                        </div>
                        <Button 
                          size="sm"
                          onClick={() => setReplyTo({ type: 'comment', ...comment })}
                        >
                          <MessageCircle className="w-4 h-4 mr-1" />
                          Responder
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Insights Tab */}
          <TabsContent value="insights" className="mt-6">
            <div className="grid gap-4">
              {insights.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    No hay datos de analytics disponibles
                  </CardContent>
                </Card>
              ) : (
                insights.map((insight) => (
                  <Card key={insight.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{insight.title || insight.name}</p>
                          <p className="text-sm text-muted-foreground">{insight.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold">
                            {insight.values?.[0]?.value?.toLocaleString() || 0}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {insight.period}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* New Post Dialog */}
      <Dialog open={showNewPost} onOpenChange={setShowNewPost}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Nueva Publicación</DialogTitle>
            <DialogDescription>
              Crea una publicación para {selectedPage?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="¿Qué quieres compartir?"
              value={newPostMessage}
              onChange={(e) => setNewPostMessage(e.target.value)}
              rows={4}
            />
            <Input
              placeholder="URL de enlace (opcional)"
              value={newPostLink}
              onChange={(e) => setNewPostLink(e.target.value)}
            />
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="schedule"
                checked={schedulePost}
                onChange={(e) => setSchedulePost(e.target.checked)}
              />
              <label htmlFor="schedule" className="text-sm">Programar publicación</label>
            </div>
            {schedulePost && (
              <Input
                type="datetime-local"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewPost(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreatePost} disabled={posting || !newPostMessage.trim()}>
              {posting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Publicando...
                </>
              ) : schedulePost ? (
                <>
                  <Clock className="w-4 h-4 mr-2" />
                  Programar
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Publicar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={!!replyTo} onOpenChange={() => setReplyTo(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {replyTo?.type === 'message' ? 'Responder mensaje' : 'Responder comentario'}
            </DialogTitle>
            <DialogDescription>
              {replyTo?.type === 'message' 
                ? `A: ${replyTo?.participants?.data?.map(p => p.name).join(', ')}`
                : `De: ${replyTo?.from?.name}`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Escribe tu respuesta..."
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyTo(null)}>
              Cancelar
            </Button>
            <Button 
              onClick={() => {
                if (replyTo?.type === 'message') {
                  handleReplyToMessage();
                } else {
                  handleReplyToComment(replyTo?.id);
                }
              }}
              disabled={replying || !replyMessage.trim()}
            >
              {replying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
